// ./src/utils/api/CrewAccessService.ts
//
// Crew EVENT ACCESS host service (docs/CREW-ACCESS.md). OpResult-native. Lets a
// crew member reach and work on an event they were added to:
//   - myCrewEvents : every event the signed-in user is crew on, across accounts.
//   - myEventAccess: the caller's { isOwner, role, capabilities } for one event —
//                    drives crew-mode nav filtering (the BE stays authoritative).
//   - crewSignIn   : a non-account crew member exchanges their invite link +
//                    password for a crew-session token (stored via CrewSessionService).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { errorMessages } from "@em/api";

import { ApiService } from "./ApiService";
import type { EventTimeframe } from "./EventService";

/** One event the caller is crew on (across accounts) — event summary + their crew role. */
export interface CrewEvent {
  id: string;
  eventGuestId?: string | null;
  guestRole?: string | null;
  accountId?: string | null;
  title?: string | null;
  slug?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** The event's time zone — its dates are shown in it (2026-10-04). */
  timezone?: string | null;
  isAllDay?: boolean | null;
  /** How guests join (link_rsvp | managed_invites | save_the_date | ticketed) — shown on the crew card (2026-10-07). */
  eventMode?: string | null;
  status?: number | null;
}

/** The caller's resolved access to one event. */
export interface EventAccess {
  eventId: string;
  isOwner: boolean;
  role: string | null;
  capabilities: string[];
  /**
   * #142 — the EVENT account's plan features (what the server's create gates read — not a crew member's own plan).
   * null/absent when unknown (ms_auth did not answer, or a gateway older than the field): the gate then fails open, as
   * the server does.
   */
  planFeatures?: EventAccessPlanFeature[] | null;
  /**
   * ROADMAP #209 — the plan bought for THIS event (partylabz sells one event at a time), or null: none bought, unknown, or
   * a gateway older than the field. A feature EITHER plan allows is allowed (the server's gate, `resolveEventGrant`).
   */
  eventUpgrade?: EventAccessUpgrade | null;
}

/** ROADMAP #209 — one event's upgrade: the plan it was bought up to, and that plan's feature values. */
export interface EventAccessUpgrade {
  planCode?: string | null;
  planName?: string | null;
  features?: EventAccessPlanFeature[] | null;
}

/** One feature of the event account's plan — the same shape as the caller's own `AppPlanFeatureDetails`. */
export interface EventAccessPlanFeature {
  featureCode: string;
  featureName?: string | null;
  featureValue?: string | null;
  featureValueType?: string | null;
  status?: number | null;
}

/** The result of a non-account crew member signing in. */
export interface CrewSignInResult {
  token: string;
  eventId: string;
  eventTitle?: string | null;
  guestRole?: string | null;
}

/** One tab and one page of My Crew Events (the API's `MyCrewEventsInput`). */
export interface MyCrewEventsParams {
  timeframe: EventTimeframe;
  page: number;
  pageSize: number;
}

const MY_CREW_EVENTS_QUERY = gql`
  query MyCrewEvents($params: MyCrewEventsInput) {
    myCrewEvents(params: $params) {
      code
      total
      errors {
        name
        errors
      }
      data {
        id
        eventGuestId
        guestRole
        accountId
        title
        slug
        startsAt
        endsAt
        timezone
        isAllDay
        eventMode
        status
      }
    }
  }
`;

const PLAN_FEATURE_FIELDS = "featureCode featureName featureValue featureValueType status";

/** `plan` adds #142's plan; `upgrade` adds #209's event upgrade too. */
const myEventAccessQuery = (withPlan: boolean, withUpgrade: boolean) => gql`
  query MyEventAccess($eventId: ID) {
    myEventAccess(eventId: $eventId) {
      code
      errors {
        name
        errors
      }
      data {
        eventId
        isOwner
        role
        capabilities${withPlan ? `\n        planFeatures { ${PLAN_FEATURE_FIELDS} }` : ""}${withUpgrade ? `\n        eventUpgrade { planCode planName features { ${PLAN_FEATURE_FIELDS} } }` : ""}
      }
    }
  }
`;

const MY_EVENT_ACCESS_QUERY = myEventAccessQuery(true, true);
const MY_EVENT_ACCESS_QUERY_PLAN = myEventAccessQuery(true, false);
const MY_EVENT_ACCESS_QUERY_BASE = myEventAccessQuery(false, false);

/** #209 — the gateway refused the read over `eventUpgrade` (not recomposed yet) — nothing else. */
export const refusedEventUpgrade = (result: OpResult): boolean =>
  !result.didSucceed() &&
  errorMessages(result).some((m) => /Cannot query field ["']eventUpgrade["'] on type ["']EventAccess["']/.test(m));

/** #142 — the gateway refused the read over `planFeatures` (not recomposed yet) — nothing else. */
export const refusedEventPlanFeatures = (result: OpResult): boolean =>
  !result.didSucceed() &&
  errorMessages(result).some((m) => /Cannot query field ["']planFeatures["'] on type ["']EventAccess["']/.test(m));

const CREW_SIGN_IN_MUTATION = gql`
  mutation CrewSignIn($inviteToken: String!, $password: String!) {
    crewSignIn(inviteToken: $inviteToken, password: $password) {
      code
      errors {
        name
        errors
      }
      data {
        token
        eventId
        eventTitle
        guestRole
      }
    }
  }
`;

export class CrewAccessService {
  /** Every event the signed-in user is crew on, across all accounts (upcoming + past). */
  /**
   * The events the signed-in user is crew on — one TAB (`timeframe`: upcoming | past, the server's All Events rule) and one
   * PAGE at a time, with the tab's `total` on the result (2026-10-31: it used to read every membership at once).
   */
  static async myCrewEvents(params: MyCrewEventsParams): Promise<OpResult> {
    return ApiService.executeQuery(MY_CREW_EVENTS_QUERY, { params }, { fetchPolicy: "network-only" });
  }

  /** The caller's access to one event → { isOwner, role, capabilities }. Omit eventId to use the crew session's own event. */
  static async myEventAccess(eventId?: string | null): Promise<OpResult> {
    const vars = { eventId: eventId ?? null };
    const full = await ApiService.executeQuery(MY_EVENT_ACCESS_QUERY, vars, { fetchPolicy: "network-only" });
    // ⚠ A gateway that does not know a field yet must cost only what reads it (the event upgrade, then the event-plan gate),
    //   never crew mode itself (this read decides the whole crew nav).
    const result = refusedEventUpgrade(full)
      ? await ApiService.executeQuery(MY_EVENT_ACCESS_QUERY_PLAN, vars, { fetchPolicy: "network-only" })
      : full;

    return refusedEventPlanFeatures(result)
      ? ApiService.executeQuery(MY_EVENT_ACCESS_QUERY_BASE, vars, { fetchPolicy: "network-only" })
      : result;
  }

  /** Exchange an invite link + crew password for a crew-session token. */
  static async crewSignIn(inviteToken: string, password: string): Promise<OpResult> {
    return ApiService.executeMutation(CREW_SIGN_IN_MUTATION, { inviteToken, password });
  }
}
